import L from "../constants/baysportLabels";
import { isExtendedNavEnabled } from "./clubMode";
import { hasActiveDemoAccess, hasFullPlanAccess } from "./demoAccess";
import { resolvePlanAccess } from "./planEntitlements";
import { isClubWebsiteAllowed } from "./clubWebsiteAccess";

/** Временно скрыть «Аналитику» у всех (маршрут сохранён). */
export const HIDE_ANALYTICS = true;

/** Главное меню */
export const CORE_ADMIN_NAV = [
  { id: "overview", to: "/manage/app", end: true, label: L.summary, icon: "overview" },
  { id: "schedule", to: "/manage/app/schedule", label: L.schedule, icon: "schedule" },
  { id: "chat", to: "/manage/app/chat", label: "Чат", icon: "chat" },
  { id: "clients", to: "/manage/app/clients", label: L.clients, icon: "clients" },
  { id: "journal", to: "/manage/app/journal", label: L.journal, icon: "journal" },
];

/** Нижняя навигация PWA */
export const ADMIN_BOTTOM_TABS = [
  ...CORE_ADMIN_NAV,
  { id: "more", to: "/manage/app/more", label: "Ещё", icon: "settings" },
];

const CORE_BOTTOM_TAB_IDS = new Set(CORE_ADMIN_NAV.map((item) => item.id));

/** Раздел «Ещё» */
export const MORE_ADMIN_NAV = [
  { id: "life", to: "/manage/app/life", label: "Клуб", icon: "life" },
  { id: "groups", to: "/manage/app/groups", label: L.groups, icon: "groups" },
  {
    id: "memberships",
    to: "/manage/app/memberships",
    label: L.memberships,
    icon: "memberships",
  },
  { id: "finance", to: "/manage/app/finance", label: "Финансы", icon: "finance" },
  {
    id: "analytics",
    to: "/manage/app/analytics",
    label: "Аналитика",
    icon: "overview",
  },
  {
    id: "sales",
    to: "/manage/app/sales",
    label: "Воронка",
    icon: "finance",
  },
  {
    id: "modules",
    to: "/manage/app/modules",
    label: "Модули",
    icon: "memberships",
  },
  { id: "venues", to: "/manage/app/venues", label: L.venues, icon: "venues" },
  {
    id: "directions",
    to: "/manage/app/directions",
    label: L.directions,
    icon: "directions",
  },
  { id: "polls", to: "/manage/app/polls", label: "Опросы", icon: "polls" },
  { id: "team", to: "/manage/app/team", label: L.team, icon: "trainers" },
];

export const ADMIN_SETTINGS_NAV = {
  id: "settings",
  to: "/manage/app/settings",
  label: "Настройки клуба",
  icon: "settings",
};

export function getClubFeatures(club, tenant, options = {}) {
  const { billing, entitlements } = options;
  const teamCount = Number(club?.team_members_count ?? 0);
  const isSolo = club?.is_solo_club ?? teamCount === 0;
  const demoFull = hasActiveDemoAccess(tenant, billing);
  const isClubPlan = hasFullPlanAccess(club, tenant, billing);
  const extended = isExtendedNavEnabled(club, tenant, billing);
  const directionsCount = Number(club?.directions_count ?? 0);
  const showDirectionsInMain =
    isClubPlan || directionsCount > 1;
  const plan = resolvePlanAccess(entitlements, tenant, billing);

  return {
    teamCount,
    isSolo,
    isClubPlan,
    demoFull,
    extended,
    directionsCount,
    // Команда (в т.ч. приглашение админов) и воронка — всем клубам
    showTeam: true,
    showDirectionsNav: showDirectionsInMain,
    showVenuesNav: true,
    showPollsNav: plan.polls,
    showAnalyticsNav: plan.analytics && !HIDE_ANALYTICS,
    showSalesNav: true,
    showChatNav: plan.chat,
    showClubLifeNav: plan.clubLife,
    showFinanceNav: plan.finance,
    showCompetitions: plan.competitions,
    showFullBranding: plan.fullBranding,
    showPortalBrandingTab: plan.showPortalBrandingTab,
    showModulesNav: isClubWebsiteAllowed(tenant),
    plan,
  };
}

function filterAdminNavItem(item, f) {
  switch (item.id) {
    case "chat":
      return f.showChatNav;
    case "life":
      return f.showClubLifeNav;
    case "finance":
      return f.showFinanceNav;
    case "analytics":
      return f.showAnalyticsNav;
    case "sales":
      return f.showSalesNav;
    case "polls":
      return f.showPollsNav;
    case "team":
      return f.showTeam;
    case "modules":
      return f.showModulesNav;
    default:
      return true;
  }
}

/** Доп. пункты в «Ещё» (зарезервировано под тарифные фичи) */
export function getMoreAdminNavExtras() {
  return [];
}

export function buildAdminSidebarNav(club, tenant, options = {}) {
  const f = getClubFeatures(club, tenant, options);
  const more = [...MORE_ADMIN_NAV, ...getMoreAdminNavExtras()].filter((item) =>
    filterAdminNavItem(item, f),
  );
  const main = CORE_ADMIN_NAV.filter((item) => filterAdminNavItem(item, f));
  const bottomTabs = [
    ...main,
    { id: "more", to: "/manage/app/more", label: "Ещё", icon: "settings" },
  ];
  return {
    main,
    more,
    footer: [ADMIN_SETTINGS_NAV],
    extended: f.extended,
    bottomTabs,
  };
}

export function resolveAdminBottomTab(pathname) {
  const tab = resolveAdminTabId(pathname);
  if (CORE_BOTTOM_TAB_IDS.has(tab)) return tab;
  return "more";
}

export function resolveAdminPageTitle(tabId, club, tenant, pathname = "") {
  if (tabId === "overview") return null;
  if (tabId === "more") return "Ещё";
  if (tabId === "notifications") return "Уведомления";
  if (tabId === "profile") return "Профиль";
  if (tabId === "modules") {
    if (String(pathname).includes("/modules/website")) return "Сайт клуба";
    return "Модули";
  }
  if (tabId === "website") return "Редактор сайта";
  if (tabId === "team" || tabId === "trainers") return L.team;
  const nav = buildAdminSidebarNav(club, tenant);
  const all = [...nav.main, ...nav.more, ...nav.footer];
  const item = all.find((entry) => entry.id === tabId);
  if (item?.label) return item.label;
  if (tabId === "trainers") return L.trainers;
  if (tabId === "appointments") return L.schedule;
  return L.adminTitle;
}

export function resolveAdminTabId(pathname) {
  const path = pathname.replace(/\/$/, "") || "/manage/app";
  if (path === "/manage/app/more") return "more";
  const ordered = [
    { id: "chat", path: "/manage/app/chat" },
    { id: "clients", path: "/manage/app/clients" },
    { id: "memberships", path: "/manage/app/memberships" },
    { id: "team", path: "/manage/app/team" },
    { id: "trainers", path: "/manage/app/trainers" },
    { id: "life", path: "/manage/app/life" },
    { id: "notifications", path: "/manage/app/notifications" },
    { id: "polls", path: "/manage/app/polls" },
    { id: "appointments", path: "/manage/app/appointments" },
    { id: "directions", path: "/manage/app/directions" },
    { id: "settings", path: "/manage/app/settings" },
    { id: "profile", path: "/manage/app/profile" },
    { id: "analytics", path: "/manage/app/analytics" },
    { id: "sales", path: "/manage/app/sales" },
    { id: "modules", path: "/manage/app/modules" },
    { id: "website", path: "/manage/app/website" },
    { id: "schedule", path: "/manage/app/schedule" },
    { id: "finance", path: "/manage/app/finance" },
    { id: "journal", path: "/manage/app/journal" },
    { id: "groups", path: "/manage/app/groups" },
    { id: "venues", path: "/manage/app/venues" },
    { id: "overview", path: "/manage/app" },
  ];
  const match = ordered.find(
    (t) => path === t.path || (t.path !== "/manage/app" && path.startsWith(`${t.path}/`)),
  );
  return match?.id || "overview";
}

export function redirectForHiddenAdminRoute(
  pathname,
  club,
  tenant,
  search = "",
  options = {},
) {
  const f = getClubFeatures(club, tenant, options);
  const path = pathname.replace(/\/$/, "");

  if (path === "/manage/app/trainers" || path === "/manage/app/trainers/") {
    return "/manage/app/team";
  }
  if (path === "/manage/app/trainers/new") {
    return "/manage/app/team/invite";
  }
  if (
    path === "/manage/app/branches" ||
    path.startsWith("/manage/app/branches/")
  ) {
    return "/manage/app/venues";
  }
  if (
    path === "/manage/app/services" ||
    path.startsWith("/manage/app/services/")
  ) {
    return "/manage/app/directions";
  }
  if (
    path === "/manage/app/appointments" ||
    path.startsWith("/manage/app/appointments/") ||
    path === "/manage/app/calendar" ||
    path.startsWith("/manage/app/calendar/") ||
    path === "/manage/app/waitlist" ||
    path.startsWith("/manage/app/waitlist/")
  ) {
    return "/manage/app/schedule";
  }
  if (
    (path === "/manage/app/polls" || path.startsWith("/manage/app/polls/")) &&
    !f.showPollsNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/analytics" ||
      path.startsWith("/manage/app/analytics/")) &&
    !f.showAnalyticsNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/sales" || path.startsWith("/manage/app/sales/")) &&
    !f.showSalesNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/modules" ||
      path.startsWith("/manage/app/modules/") ||
      path === "/manage/app/website" ||
      path.startsWith("/manage/app/website/")) &&
    !f.showModulesNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/chat" || path.startsWith("/manage/app/chat/")) &&
    !f.showChatNav
  ) {
    return "/manage/app";
  }
  if (
    (path === "/manage/app/life" || path.startsWith("/manage/app/life/")) &&
    !f.showClubLifeNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/finance" || path.startsWith("/manage/app/finance/")) &&
    !f.showFinanceNav
  ) {
    return "/manage/app/more";
  }
  if (
    (path === "/manage/app/team" || path.startsWith("/manage/app/team/")) &&
    !f.showTeam
  ) {
    return "/manage/app/more";
  }
  return null;
}

/** Ссылка на страницу журнала посещаемости */
export function journalUrl({ date, sessionId, groupId } = {}) {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (sessionId) params.set("session", sessionId);
  if (groupId) params.set("group", groupId);
  const qs = params.toString();
  return qs ? `/manage/app/journal?${qs}` : "/manage/app/journal";
}

/** Ссылка на календарь занятий (вкладка «Календарь») */
export function scheduleUrl({
  date,
  sessionId,
  groupId,
  trainerId,
  view = "month",
} = {}) {
  const params = new URLSearchParams();
  params.set("view", view);
  if (date) params.set("date", date);
  if (sessionId) params.set("session", sessionId);
  if (groupId) params.set("group", groupId);
  if (trainerId) params.set("trainer", trainerId);
  return `/manage/app/schedule?${params.toString()}`;
}
